import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Loader2, MessageSquare, Play, Repeat, Sparkles, Users, Workflow } from 'lucide-react';
import { AppChrome, toolLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-361" data-mp="1yg8a392ra226">
      <SectionIntro className="mx-auto max-w-3xl text-center" data-id="element-362" data-mp="1yjw223sjyges">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-363" data-mp="1thom734lqg76">
          Workspace
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-364" data-mp="28f7icqjlzvon">
          One place to manage
          <br data-id="element-365" data-mp="e1g052n7s3hl" />
          AI within your company
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-366" data-mp="e427p2a3uizt">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-367" data-mp="17roumyr8d6le">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1" data-id="element-368" data-mp="1hpcz4gen1sgr">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-369" data-mp="1lpdf45hdybpx">
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`w-full rounded-2xl px-4 py-3.5 text-left transition-colors ${on ? 'bg-white shadow-[0_18px_44px_-30px_rgba(43,33,26,0.5)] ring-1 ring-[#E7E0D4]' : 'hover:bg-white/60'}`} data-id="element-370" data-mp="1qigc6nldg3av">
                  <span className="flex items-center gap-2.5" data-id="element-371" data-mp="25fguy1ll4nx7">
                    <p.icon size={17} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-372" data-mp="25fp6nq5z1n6t" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-373" data-mp="3pz2mk7bh09wc">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-374" data-mp="3g1eksxfg624o">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        <AppChrome active={active === 'pulses' ? 'Pulses' : active === 'shared' ? 'Groups' : undefined} data-id="element-375" data-mp="3tytcy9x34o0c">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className="px-5 py-5 md:px-7 md:py-6" data-id="element-376" data-mp="vb5wzt72n2a3">
            {active === 'chats' && <ChatsScreen data-id="element-377" data-mp="fxd2dmaveapu" />}
            {active === 'shared' && <SharedScreen data-id="element-378" data-mp="12ynnxyc9ngz" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-379" data-mp="14jmw0vllul6" />}
            {active === 'pulses' && <PulsesScreen data-id="element-380" data-mp="621k4u096ldc" />}
            {active === 'skills' && <SkillsScreen data-id="element-381" data-mp="tquiex4cl5b4" />}
          </motion.div>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  return <div className="mb-5" data-id="element-382" data-mp="ys0cay9quym8">
      <h3 className="font-title text-[21px] font-medium tracking-tight text-[#2B211A]" data-id="element-383" data-mp="k1ayrp8t97f7">
        {title}
      </h3>
      <p className="mt-1 text-[12.5px] text-neutral-500" data-id="element-384" data-mp="ozck6gtplitu">{meta}</p>
    </div>;
}
function ChatsScreen() {
  return <div data-id="element-385" data-mp="1cono276cvtc7">
      <ScreenHead title="Renewal risk in Salesforce" meta="Giga Master · 3 sources" data-id="element-386" data-mp="1cox4p45igo58" />
      <div className="flex justify-end" data-id="element-387" data-mp="1hhgsetxwwznr">
        <p className="max-w-[80%] rounded-2xl bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-388" data-mp="1hii6766bc096">
          Which renewals due this quarter look at risk?
        </p>
      </div>
      <div className="mt-4 space-y-1.5" data-id="element-389" data-mp="2x7hg6qziwxfs">
        {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned the last 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12.5px] text-neutral-500" data-id="element-390" data-mp="2j7q816voub9p">
            <img src={toolLogo(slug)} alt="" aria-hidden="true" className="size-3.5 shrink-0 object-contain" data-id="element-391" data-mp="2e954oyl53bh1" />
            {label}
            <Check size={12} strokeWidth={3} className="text-emerald-500" data-id="element-392" data-mp="2s9pkdlvrcr6b" />
          </p>)}
      </div>
      <div className="mt-4 space-y-3 text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-393" data-mp="2o5zyj46gwnoz">
        <p data-id="element-394" data-mp="7hnuzgrmlxal">
          <strong className="font-semibold text-[#2B211A]" data-id="element-395" data-mp="7he47qyrbm3u">
            Three of eleven look at risk.
          </strong>{' '}
          Northwind has had no exec contact in 41 days and two unresolved
          tickets. Meridian's champion left in July. Fairline's usage is down
          38% month over month.
        </p>
        <p className="text-neutral-500" data-id="element-396" data-mp="318uaao3dn65a">
          I've drafted outreach for each owner — want me to send it?
        </p>
      </div>
      <div className="mt-5 flex flex-wrap gap-1.5 border-t border-[#EFEAE1] pt-3.5" data-id="element-397" data-mp="3ab3g50npabcc">
        {['34 opportunities', '12 tickets', '60 days of mail'].map(s => <span key={s} className="rounded-full border border-[#EFEAE1] px-2.5 py-1 text-[11.5px] font-medium text-neutral-500" data-id="element-398" data-mp="2dm46bh7gx1i1">
            {s}
          </span>)}
      </div>
    </div>;
}
function SharedScreen() {
  const groups = [{
    name: 'Giga Master',
    initials: 'GM',
    color: '#134E4A',
    members: 18,
    chats: 214,
    tracks: 33
  }, {
    name: 'Giga Marketing',
    initials: 'GM',
    color: '#7C2D12',
    members: 6,
    chats: 88,
    tracks: 12
  }, {
    name: 'NoContact Ops',
    initials: 'NO',
    color: '#1E3A5F',
    members: 4,
    chats: 51,
    tracks: 9
  }, {
    name: 'Everyone',
    initials: 'EV',
    color: '#3F3A2E',
    members: 18,
    chats: 106,
    tracks: 4
  }];
  return <div data-id="element-399" data-mp="1me3zxswoxa9y">
      <ScreenHead title="Groups" meta="Access follows the group, not the item" data-id="element-400" data-mp="1mb81m3gxkjkz" />
      <ul className="space-y-2" data-id="element-401" data-mp="1ia64ptahkjm6">
        {groups.map(g => <li key={g.name} className="flex items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-402" data-mp="1dd5vxam952rd">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-xl text-[10.5px] font-bold text-white" style={{
          backgroundColor: g.color
        }} data-id="element-403" data-mp="18dt8oorgxto9">
              {g.initials}
            </span>
            <span className="min-w-0 flex-1" data-id="element-404" data-mp="yo9p1gvxlvsc">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-405" data-mp="umeq2j1h3uhq">
                {g.name}
              </span>
              <span className="font-mono text-[11px] text-neutral-400" data-id="element-406" data-mp="pm0j74blyqla">
                spaces/{g.name.toLowerCase().replace(/\s+/g, '-')}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-407" data-mp="28k52myb3qme8">
              {[[g.members, 'members'], [g.chats, 'chats'], [g.tracks, 'tracks']].map(([n, l]) => <span key={String(l)} title={String(l)} className="rounded-lg bg-[#F7F4EE] px-2 py-1 text-[11px] font-semibold text-neutral-500" data-id="element-408" data-mp="2dhnhf5bsvvu5">
                  {n}
                </span>)}
            </span>
          </li>)}
      </ul>
    </div>;
}
function WorkflowsScreen() {
  const steps = ['Pull every deal that closed last week from Salesforce', 'Match each to its support and usage history', 'Write the win/loss summary into the Notion database', 'Post the digest to #revenue'];
  return <div data-id="element-409" data-mp="1gv1ydij7tc1l">
      <ScreenHead title="Weekly win/loss digest" meta="4 steps · 26 runs · 96% success" data-id="element-410" data-mp="1gre6entf4xoz" />
      <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-411" data-mp="1kvc6p1uhcejm">
        {[['Reporting period', 'Date · required'], ['Notion database', 'Database · required'], ['Owner', 'Text · optional']].map(([k, v]) => <span key={k} className="rounded-xl border border-[#EFEAE1] px-2.5 py-1.5" data-id="element-412" data-mp="1bt2zolyf73az">
            <span className="block text-[12px] font-semibold text-[#2B211A]" data-id="element-413" data-mp="s9nijscptmh2">
              {k}
            </span>
            <span className="text-[10.5px] text-neutral-400" data-id="element-414" data-mp="nb2hxrtjx0s3">{v}</span>
          </span>)}
      </div>
      <ol className="space-y-2" data-id="element-415" data-mp="128d4bw7qfr2l">
        {steps.map((s, i) => <li key={s} className="flex gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-2.5" data-id="element-416" data-mp="x7ywofa1mj53" data-mpi={i}>
            <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-[#F4F0E9] text-[11px] font-bold text-[#2B211A]" data-id="element-417" data-mp="1b76wnae15vac">
              {i + 1}
            </span>
            <span className="text-[13px] leading-snug text-[#3D3229]" data-id="element-418" data-mp="1kx9vvsbqw5h4">{s}</span>
          </li>)}
      </ol>
      <div className="mt-4 flex items-center gap-2 border-t border-[#EFEAE1] pt-3.5" data-id="element-419" data-mp="1fygkc5g0qojl">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-bold text-white" data-id="element-420" data-mp="1txy0xxj5uvhv">
          <Play size={11} data-id="element-421" data-mp="1px5qj0yjykk0" />
          Run now
        </span>
        <span className="text-[12px] text-neutral-500" data-id="element-422" data-mp="1pujjd2fkq72b">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
function PulsesScreen() {
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  return <div data-id="element-423" data-mp="3k5ydzhfxiy0v">
      <ScreenHead title="Pulses" meta="4 enabled · work that runs without being asked" data-id="element-424" data-mp="3k6873dw9tlas" />
      <ul className="space-y-2" data-id="element-425" data-mp="2cuhgipgyjizc">
        {pulses.map(p => <li key={p.name} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-426" data-mp="28rkt8lgh2yp2">
            <span className="min-w-0 flex-1" data-id="element-427" data-mp="oabo967aib9b">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-428" data-mp="o77fdvivqrcb">
                {p.name}
              </span>
              <span className="text-[11.5px] text-neutral-500" data-id="element-429" data-mp="x9gl8837dwjd">
                {p.cadence}
              </span>
            </span>
            {/* Twelve ticks say "is this healthy" faster than a percentage. */}
            <span className="flex shrink-0 items-end gap-[3px]" data-id="element-430" data-mp="16yqdpp1ylh80">
              {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${h === 1 ? 'bg-emerald-400' : h === 0 ? 'bg-red-400' : 'bg-amber-400'}`} data-id="element-431" data-mp="1by3c97270cqf" data-mpi={i} />)}
            </span>
            <span className="flex shrink-0 items-center gap-1.5 text-[11.5px] font-medium text-neutral-500" data-id="element-432" data-mp="1fyn9eamrngck">
              <Clock size={11} className="text-neutral-300" data-id="element-433" data-mp="3hrunxhuk1txe" />
              {p.next}
            </span>
          </li>)}
      </ul>
      <div className="mt-3 flex items-center gap-2 rounded-2xl bg-[#FBF8F3] px-3.5 py-2.5 text-[12.5px] text-neutral-500" data-id="element-434" data-mp="3mq4j3sga4uj0">
        <Loader2 size={12} className="animate-spin text-[#EA580C]" data-id="element-435" data-mp="tx8rep1hggox" />
        Support escalation sweep is running — step 2 of 4
      </div>
    </div>;
}
function SkillsScreen() {
  const skills = [{
    name: 'How we qualify an inbound lead',
    used: 142,
    group: 'Giga Marketing'
  }, {
    name: 'Our weekly reporting format',
    used: 96,
    group: 'Giga Master'
  }, {
    name: 'Escalating a P1 incident',
    used: 38,
    group: 'NoContact Ops'
  }, {
    name: 'Drafting a renewal email',
    used: 211,
    group: 'Giga Master'
  }];
  return <div data-id="element-436" data-mp="1lndi15fu5zyl">
      <ScreenHead title="Skills" meta="Recorded once, reused everywhere" data-id="element-437" data-mp="1lo4z7b8orv5c" />
      <ul className="space-y-2" data-id="element-438" data-mp="1hm0a8pt6nmp1">
        {skills.map(s => <li key={s.name} className="flex items-center gap-3 rounded-2xl border border-[#EFEAE1] px-3.5 py-3" data-id="element-439" data-mp="1hnbdmoipusfm">
            <Sparkles size={15} className="shrink-0 text-[#EA580C]" data-id="element-440" data-mp="1vmaz5naxmr7k" />
            <span className="min-w-0 flex-1" data-id="element-441" data-mp="1qnhpy72ohmd7">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-442" data-mp="1qklquff3grn6">
                {s.name}
              </span>
              <span className="text-[11.5px] text-neutral-500" data-id="element-443" data-mp="2f84id9pqikij">{s.group}</span>
            </span>
            <span className="shrink-0 text-[11.5px] font-medium text-neutral-400" data-id="element-444" data-mp="20atxxav464ao">
              used {s.used}×
            </span>
          </li>)}
      </ul>
      <p className="mt-3 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-445" data-mp="257kgq5yazdzs">
        Every chat, workflow, and pulse in the workspace draws on these, so the
        method stays consistent no matter who asks.
      </p>
    </div>;
}